import React, { useState, useEffect } from 'react';
import { useDashboard } from '../../context/DashboardContext';
import api from '../../api';
import readMoreIcon from '../../assets/read_more.svg';
import NewsFeedSkeleton from './NewsFeedSkeleton';
import BackButton from '../Common/BackButton';
import { useI18n } from '../../i18n';

const NewsFeedSection = () => {
  const { t } = useI18n();
  const { setActiveView } = useDashboard();
  const [feeds, setFeeds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedFeedIndex, setSelectedFeedIndex] = useState(0);

  const [selectedCategory, setSelectedCategory] = useState('All');
  const [activeButton, setActiveButton] = useState('All');

  useEffect(() => {
    fetchNews();
  }, []);

  const handleCategoryClick = (category, query, buttonName) => {
    setActiveButton(buttonName);
    fetchNews(query, category);
  };

  const fetchNews = async (query = '', categoryLabel = 'All') => {
    setLoading(true);
    setSelectedCategory(categoryLabel);
    try {
      const endpoint = query
        ? `/v1/news/search?q=${encodeURIComponent(query)}`
        : '/v1/news';
      const response = await api.get(endpoint);
      if (response.status) {
        setFeeds(Array.isArray(response.data) ? response.data : []);
        setSelectedFeedIndex(0);
      }
    } catch (error) {
      console.error('Failed to fetch news:', error);
    } finally {
      setLoading(false);
    }
  };

  const getSourceName = (feed) => {
    if (!feed) return '';
    return feed.source && typeof feed.source === 'object'
      ? feed.source.name
      : feed.source || t('pages.news_feed.title');
  };

  const getSourceLogo = (feed) => {
    if (!feed) return '';
    const name = getSourceName(feed).toLowerCase();
    if (name.includes('cnbc'))
      return 'https://upload.wikimedia.org/wikipedia/commons/e/e1/CNBC_logo.svg';
    if (name.includes('cnn'))
      return 'https://upload.wikimedia.org/wikipedia/commons/b/b1/CNN.svg';
    if (name.includes('bbc'))
      return 'https://upload.wikimedia.org/wikipedia/commons/4/4e/BBC_News_2019.svg';

    // Return a generic logo or the article image if no source logo matches
    return `https://ui-avatars.com/api/?name=${encodeURIComponent(getSourceName(feed))}&background=0D8ABC&color=fff`;
  };

  const selectedFeedData = feeds[selectedFeedIndex] || null;

  if (loading && feeds.length === 0) {
    return <NewsFeedSkeleton />;
  }

  return (
    <div className="news-feed news-feed-section-container">
      <div className="section-header">
        <h2 className="section-title">
          <BackButton onClick={() => setActiveView('dashboard')} ariaLabel={t('common.navigation.back_to_dashboard')} />
          {t('pages.news_feed.title')}
        </h2>
      </div>
      <div className="news-feed-buttons">
        <button
          className={`news-feed-button ${activeButton === 'All' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('All', '', 'All')}
        >
          {t('pages.news_feed.categories.all')}
        </button>
        <button
          className={`news-feed-button ${activeButton === 'Business' ? 'active' : ''}`}
          onClick={() =>
            handleCategoryClick('Business', 'business', 'Business')
          }
        >
          {t('pages.news_feed.categories.business')}
        </button>
        <button
          className={`news-feed-button ${activeButton === 'Finance' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('Finance', 'finance', 'Finance')}
        >
          {t('pages.news_feed.categories.finance')}
        </button>
        <button
          className={`news-feed-button ${activeButton === 'Technology' ? 'active' : ''}`}
          onClick={() =>
            handleCategoryClick('Technology', 'technology', 'Technology')
          }
        >
          {t('pages.news_feed.categories.technology')}
        </button>
        <button
          className={`news-feed-button ${activeButton === 'Science' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('Science', 'science', 'Science')}
        >
          {t('pages.news_feed.categories.science')}
        </button>
        <button
          className={`news-feed-button ${activeButton === 'Health' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('Health', 'health', 'Health')}
        >
          {t('pages.news_feed.categories.health')}
        </button>
        <button
          className={`news-feed-button ${activeButton === 'Entertainment' ? 'active' : ''}`}
          onClick={() =>
            handleCategoryClick(
              'Entertainment',
              'entertainment',
              'Entertainment'
            )
          }
        >
          {t('pages.news_feed.categories.entertainment')}
        </button>
        <button
          className={`news-feed-button ${activeButton === 'Sports' ? 'active' : ''}`}
          onClick={() => handleCategoryClick('Sports', 'sports', 'Sports')}
        >
          {t('pages.news_feed.categories.sports')}
        </button>
      </div>

      <div className="news-feed-wrapper">
        <div className="news-feed-layout">
          {/* Left Column - Feed List */}
          <div className="feed-list-column">
            <div className="feed-list">
              {feeds.length > 0 ? (
                feeds.map((feed, index) => (
                  <div
                    key={index}
                    className={`feed-item ${selectedFeedIndex === index ? 'active' : ''}`}
                    onClick={() => setSelectedFeedIndex(index)}
                  >
                    <div className="feed-logo">
                      <img
                        src={getSourceLogo(feed)}
                        alt={getSourceName(feed)}
                      />
                    </div>
                    <div className="feed-content">
                      <div className="feed-title">
                        {feed.title && feed.title?.length > 40
                          ? feed.title.substring(0, 40) + '...'
                          : feed.title}
                      </div>
                      <div className="feed-description">
                        {feed.description ||
                          feed.content ||
                          t('pages.news_feed.no_description')}
                      </div>
                      <div className="feed-meta">{feed.publishedAt}</div>
                    </div>
                  </div>
                ))
              ) : (
                <div className="no-news">{t('pages.news_feed.no_news')}</div>
              )}
            </div>
          </div>

          {/* Right Column - Feed Content */}
          <div className="feed-content-column">
            <div className="feed-content-header">
              <h2 className="feed-content-title">{selectedFeedData?.title}</h2>
            </div>

            {selectedFeedData ? (
              <div className="feed-content-body">
                <div className="feed-content-meta-top">
                  {selectedFeedData.urlToImage && (
                    <div className="feed-article-image">
                      <img
                        src={selectedFeedData.urlToImage}
                        alt="Article"
                        onError={(e) => (e.target.style.display = 'none')}
                      />
                    </div>
                  )}
                </div>

                <div className="feed-content-text">
                  {selectedFeedData.content ||
                    selectedFeedData.description ||
                    t('pages.news_feed.no_content')}
                  {selectedFeedData.url && (
                    <div className="read-more">
                      <a
                        href={selectedFeedData.url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {t('pages.news_feed.read_full', { source: getSourceName(selectedFeedData) })}
                        <img
                          src={readMoreIcon}
                          alt=""
                          className="read-more-icon"
                        />
                      </a>
                    </div>
                  )}
                </div>
              </div>
            ) : (
              <div className="feed-empty-state">{t('pages.news_feed.select_article')}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default NewsFeedSection;
